import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';
import { TeamListRowData } from '../team-list-row/team-list-row';

/**
 * Re-export TeamListRowData as TeamCardData for consistency
 */
export type TeamCardData = TeamListRowData;

/**
 * TeamCard Component
 *
 * Displays a team's basic information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-team-card
 *   [team]="teamData"
 *   [editRoute]="['/teams', teamData.id]"
 *   (cardClick)="onTeamSelected($event)"
 *   (deleteClick)="onDeleteTeam($event)">
 * </zag-team-card>
 * ```
 */
@Component({
  selector: 'zag-team-card',
  templateUrl: './team-card.html',
  styleUrls: ['./team-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon],
  host: {
    '[class.zag-team-card]': 'true',
  },
})
export class TeamCard {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The team data to display */
  readonly team = input.required<TeamCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the card is clicked */
  readonly cardClick = output<TeamCardData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<TeamCardData>();

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.team());
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.team());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
